import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 購物車 Zustand Store
const useCartStore = create(
  persist(
    (set, get) => ({
      // 狀態
      items: [],
      discountCode: null,
      
      // 加入商品到購物車
      addItem: (product, quantity = 1) => {
        set((state) => {
          const existingItem = state.items.find(item => item.id === product.id);
          
          if (existingItem) {
            // 如果商品已存在，增加數量
            return {
              items: state.items.map(item =>
                item.id === product.id
                  ? { ...item, quantity: item.quantity + quantity }
                  : item
              )
            };
          } else {
            // 如果商品不存在，加入新商品
            return {
              items: [
                ...state.items,
                {
                  id: product.id,
                  sku: product.sku,
                  name: product.name,
                  shortName: product.shortName,
                  price: product.pricing.salePrice,
                  originalPrice: product.pricing.originalPrice,
                  image: product.images[0]?.url || '',
                  quantity: quantity,
                  bulkDiscounts: product.bulkDiscounts,
                }
              ]
            };
          }
        });
      },
      
      // 更新商品數量
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        
        set((state) => ({
          items: state.items.map(item =>
            item.id === productId
              ? { ...item, quantity }
              : item
          )
        }));
      },
      
      // 移除商品
      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter(item => item.id !== productId)
        }));
      },
      
      // 清空購物車
      clearCart: () => {
        set({ items: [], discountCode: null });
      },
      
      // 套用折扣碼
      applyDiscountCode: (code, amount, type = 'fixed') => {
        set({ discountCode: { code, amount, type } });
      },
      
      // 移除折扣碼
      removeDiscountCode: () => {
        set({ discountCode: null });
      },
      
      // 取得總商品數量
      getTotalItems: () => {
        const { items } = get();
        return items.reduce((total, item) => total + item.quantity, 0);
      },
      
      // 計算團購折扣率
      getBulkDiscountRate: () => {
        const { items } = get();
        if (items.length === 0) return 1;
        
        const totalQuantity = get().getTotalItems();
        const firstItem = items[0];
        const discounts = firstItem.bulkDiscounts || {};
        
        // 找出適用的折扣（取最大的適用數量）
        let applicableRate = 1;
        const sortedThresholds = Object.keys(discounts)
          .map(Number)
          .sort((a, b) => b - a);
        
        for (const threshold of sortedThresholds) {
          if (totalQuantity >= threshold) {
            applicableRate = discounts[threshold];
            break;
          }
        }
        
        return applicableRate;
      },
      
      // 取得團購折扣資訊
      getBulkDiscountInfo: () => {
        const { items } = get();
        if (items.length === 0) {
          return {
            applicable: false,
            threshold: 0,
            rate: 1,
            discountPercentage: 0,
            message: '',
            savings: 0
          };
        }
        
        const totalQuantity = get().getTotalItems();
        const firstItem = items[0];
        const discounts = firstItem.bulkDiscounts || {};
        
        const sortedThresholds = Object.keys(discounts)
          .map(Number)
          .sort((a, b) => b - a);
        
        for (const threshold of sortedThresholds) {
          if (totalQuantity >= threshold) {
            const rate = discounts[threshold];
            const discountPercentage = Math.round((1 - rate) * 100);
            const subtotal = get().getSubtotal();
            const discountedTotal = get().getTotalPrice();
            const savings = subtotal - discountedTotal;
            
            return {
              applicable: true,
              threshold,
              rate,
              discountPercentage,
              message: `${threshold} 件享 ${Math.round(rate * 10)} 折優惠`,
              savings
            };
          }
        }
        
        // 提示下一個折扣門檻
        const nextThreshold = sortedThresholds.find(t => t > totalQuantity) || sortedThresholds[sortedThresholds.length - 1];
        if (nextThreshold) {
          const nextRate = discounts[nextThreshold];
          const remaining = nextThreshold - totalQuantity;
          
          return {
            applicable: false,
            threshold: nextThreshold,
            rate: nextRate,
            discountPercentage: Math.round((1 - nextRate) * 100),
            message: `再買 ${remaining} 件享 ${Math.round(nextRate * 10)} 折`,
            savings: 0
          };
        }
        
        return {
          applicable: false,
          threshold: 0,
          rate: 1,
          discountPercentage: 0,
          message: '',
          savings: 0
        };
      },
      
      // 計算小計（未套用折扣）
      getSubtotal: () => {
        const { items } = get();
        return items.reduce((total, item) => {
          return total + (item.price * item.quantity);
        }, 0);
      },
      
      // 計算團購折扣金額
      getBulkDiscountAmount: () => {
        const subtotal = get().getSubtotal();
        const rate = get().getBulkDiscountRate();
        const discountedTotal = Math.round(subtotal * rate);
        return subtotal - discountedTotal;
      },
      
      // 計算折扣碼折扣金額
      getDiscountCodeAmount: () => {
        const { discountCode } = get();
        if (!discountCode) return 0;
        
        if (discountCode.type === 'fixed') {
          return discountCode.amount;
        } else if (discountCode.type === 'percentage') {
          const subtotal = get().getSubtotal();
          const rate = get().getBulkDiscountRate();
          const afterBulkDiscount = Math.round(subtotal * rate);
          return Math.round(afterBulkDiscount * (discountCode.amount / 100));
        }
        
        return 0;
      },
      
      // 計算運費
      // 注意：購物車階段無法得知付款方式，這裡僅供參考
      // 實際運費會在結帳頁面根據付款方式動態計算
      getShippingCost: (paymentMethod = 'credit_card') => {
        const total = get().getTotalPrice();
        
        // 🚚 貨到付款運費規則
        if (paymentMethod === 'cod') {
          // 滿 NT$3,000 免運費
          if (total >= 3000) {
            return 0;
          }
          // 未滿 NT$3,000 收取 NT$60 運費
          return 60;
        }
        
        // 💳 線上付款全部免運
        return 0;
      },
      
      // 計算總金額（含所有折扣）
      getTotalPrice: () => {
        const subtotal = get().getSubtotal();
        const rate = get().getBulkDiscountRate();
        const afterBulkDiscount = Math.round(subtotal * rate);
        const discountCodeAmount = get().getDiscountCodeAmount();
        const shipping = get().getShippingCost();
        
        const total = afterBulkDiscount - discountCodeAmount + shipping;
        return Math.max(0, total); // 確保不會是負數
      },
      
      // 取得購物車摘要
      getCartSummary: () => {
        const items = get().items;
        const subtotal = get().getSubtotal();
        const bulkDiscountRate = get().getBulkDiscountRate();
        const bulkDiscountAmount = get().getBulkDiscountAmount();
        const bulkDiscountInfo = get().getBulkDiscountInfo();
        const discountCodeAmount = get().getDiscountCodeAmount();
        const shipping = get().getShippingCost();
        const total = get().getTotalPrice();
        const totalItems = get().getTotalItems();
        
        return {
          items,
          totalItems,
          subtotal,
          bulkDiscountRate,
          bulkDiscountAmount,
          bulkDiscountInfo,
          discountCodeAmount,
          discountCode: get().discountCode,
          shipping,
          total,
          savings: subtotal - total + shipping
        };
      },
      
      // 檢查購物車是否為空
      isEmpty: () => {
        return get().items.length === 0;
      },
      
      // 檢查商品是否在購物車中
      hasItem: (productId) => {
        return get().items.some(item => item.id === productId);
      },
      
      // 取得商品在購物車中的數量
      getItemQuantity: (productId) => {
        const item = get().items.find(item => item.id === productId);
        return item ? item.quantity : 0;
      },
    }),
    {
      name: 'nmn-cart-storage', // localStorage 的 key 名稱
      partialize: (state) => ({
        items: state.items,
        discountCode: state.discountCode,
      }), // 只持久化這些欄位
    }
  )
);

export default useCartStore;
